/*
Public (signed-out) route predicates (XOL-602/603/604).

One definition of which paths belong to the login surface, shared by the router guard
(lib/authGuard.js) and sanitizeRedirectTarget (lib/auth.js). Two places previously spelled
the /login and /logout prefix rules out by hand; five more paths join them here, three of
which carry a single-use secret in the URL (a password link or an invitation).

All predicates take a PATH (no origin, no '#'): `to.path`, or a stored redirect target.
They match one whole segment, then '/' or '?': `/login`, `/login/x`, `/login?x`, but not
`/loginx`. Matching is case-insensitive because vue-router matches routes that way, so
`/Reset-Password/<token>` is the same page as `/reset-password/<token>` and must be treated
the same by the guard and by the redirect sanitiser.

THE SIGNED-OUT-ONLY RULE (D11). The password-link, invite-link, forgot and password-set
paths, like /login, are for signed-out browsers only. A signed-in browser that opens one is
sent to the app and the page never renders, so a link can never be used from a live session
(and a captured link clicked from a preview does nothing there).

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

function lowerPath (path) {
  return (typeof path === 'string' ? path : '').toLowerCase()
}

// `base` itself, `base/...` or `base?...`. `base` is lower case.
function atSegment (path, base) {
  var p = lowerPath(path)
  return p === base || p.indexOf(base + '/') === 0 || p.indexOf(base + '?') === 0
}

export function isLoginPath (path) {
  return atSegment(path, '/login')
}

export function isLogoutPath (path) {
  return atSegment(path, '/logout')
}

export function isForgotPasswordPath (path) {
  return atSegment(path, '/forgot-password')
}

// /reset-password/<token> (a reset) and /set-password/<token> (an invitation to set a
// password). The token is a path parameter, so these must never be stored anywhere.
export function isPasswordLinkPath (path) {
  return atSegment(path, '/reset-password') || atSegment(path, '/set-password')
}

// /register/<token>: an invitation to register.
export function isInviteLinkPath (path) {
  return atSegment(path, '/register')
}

// /password-set: the success page, shown from in-memory state only.
export function isPasswordSetPath (path) {
  return atSegment(path, '/password-set')
}

// The four pages that exist for a signed-out browser only (besides /login itself).
export function isSignedOutOnlyPath (path) {
  return isForgotPasswordPath(path) ||
    isPasswordLinkPath(path) ||
    isInviteLinkPath(path) ||
    isPasswordSetPath(path)
}

// Every path that must never become a post-login redirect target: the login surface
// (we never bounce back into auth) and anything carrying a link token.
export function isAuthSurfacePath (path) {
  return isLoginPath(path) || isLogoutPath(path) || isSignedOutOnlyPath(path)
}
